import React, { useContext, useEffect, useMemo, useRef } from "react";
import { Group } from "@iventis/types";
import { useObservableValue } from "@iventis/utilities";
import { ItemRenderer, RowHeaderLayout, SingleColumnRowRefs, SingleColumnRowRefsCluster, SingleColumnRowRefsNested } from "../interactive-grid.types";
import type { IGridItem } from "../interactive-grid.types";
import { IGridContext } from "../state/interactive-grid-context";
import { clusterHeight } from "../utilities/constants";
import { getRowBorder, gridTemplateRowsFromRowGridLayout, isWideRow, rowGridLayoutFromGroups, columnGridLayoutFromGroups } from "../utilities/helpers";

/**
 * Interactive Grid Body
 * Holds the main pannable content or "background"
 * Pans in both the X and Y directions
 */
export const IGridBody: React.FC<{
    xGroups: Group[];
    items: IGridItem[];
    offsetX: number;
    offsetY: number;
    groupColumnWidth: number;
    renderItem: ItemRenderer;
}> = ({ xGroups, items, offsetX, offsetY, groupColumnWidth, renderItem }) => {
    const translation = useObservableValue(useContext(IGridContext).translation);

    const ctx = useContext(IGridContext);

    const rowHeaderGroups = useObservableValue(ctx.rowHeaderGroups);

    const ref = useRef<HTMLDivElement>();

    const panning = useObservableValue(ctx.panning);

    const transformation = useMemo(() => (translation ? `translate(${translation[0]}px, ${translation[1]}px)` : `translate(0, 0)`), [translation?.[0], translation?.[1]]);

    const rows = useMemo(() => rowGridLayoutFromGroups(rowHeaderGroups || []), [rowHeaderGroups]);

    const columns = useMemo(() => columnGridLayoutFromGroups(xGroups, groupColumnWidth), [xGroups]);

    const singleColumnElements = useRef([]);

    /*
        Further down, we will calculate the height of each row. For this, we only need to use a 'single column'.
        Therefore here, we set up the structure that will remember the index of the ref in the singleColumnElements ref array.
        This basically links together singleColumnElements and eventBlockContainers.
        You will notice that this looping structure mirrors the one further down which defines the event block containers.
    */
    const singleColumnRowRefs: SingleColumnRowRefs = useMemo(() => {
        // We have to define an explicit index, since we're using a tree of depth 2, and the index needs to be 'flat' since we're pushing to a one-dimensional array.
        let index = 0;
        return rows.map((row) => {
            if (isWideRow(row)) {
                // Create a ref index for a wide row
                const refEntry: SingleColumnRowRefsCluster = { groupId: row.groupId, wideCluster: true, refIndex: index };
                index += 1;
                return refEntry;
            }
            const children = row.children.map((child) => {
                // Create a ref index for a regular row (which would contain our grid items)
                const refEntry = { groupId: child.groupId, refIndex: index };
                index += 1;
                return refEntry;
            });
            const refEntry: SingleColumnRowRefsNested = { groupId: row.groupId, wideCluster: false, children };
            return refEntry;
        });
    }, [rows]);

    useEffect(() => {
        ref.current.style.transform = transformation;
    }, [transformation, ref.current]);

    const columnBackgrounds = columns
        .map(({ groupId: columnId, columnNumber }) => (
            <div key={columnId} style={{ gridColumn: columnNumber, gridRow: "1 / -1", backgroundColor: ctx.colours.value.alternateColumnColour }} />
        ))
        .filter((_, index) => index % 2);

    // Create all the event block containers. Each event block container has multiple event blocks within it.
    // Each event block has a specified position on the grid, relating to its column and row group.
    const eventBlockContainers: React.ReactElement[] = useMemo(() => {
        const eventBlockContainers: React.ReactElement[] = [];

        let rowIndex = 0;

        rows.forEach((row, topLevelRowIndex) => {
            if (isWideRow(row)) {
                /*
                    Redeclare the row index, since it will need to remain the same for this iteration
                    of the loop when the ref={} callback is finally invoked. Otherwise, we will only
                    be referencing the index of the final iteration
                */
                const scopedRowIndex = rowIndex;
                eventBlockContainers.push(
                    <div
                        ref={(el) => {
                            singleColumnElements.current[scopedRowIndex] = el;
                        }}
                        key={row.groupId}
                        style={{
                            height: clusterHeight,
                            gridRow: row.rowNumber,
                            gridColumnStart: 1,
                            /* Wide clusters span all the way to the end */
                            gridColumnEnd: columns[columns.length - 1].columnNumber + 1,
                        }}
                    />
                );
                rowIndex += 1;
            }

            if (!isWideRow(row)) {
                row.children.forEach((child, childIndex) => {
                    columns.forEach(({ groupId: columnId, columnNumber }, columnIndex) => {
                        const scopedRowIndex = rowIndex;
                        eventBlockContainers.push(
                            <div
                                ref={(el) => {
                                    if (columnIndex === 0) {
                                        singleColumnElements.current[scopedRowIndex] = el;
                                    }
                                }}
                                key={columnId + child.groupId}
                                style={{
                                    ...(childIndex !== 0 && { boxShadow: getRowBorder(ctx.colours.value.thirdLevelRowHeaderLineColour) }),
                                    ...(childIndex === 0 &&
                                        !rows[topLevelRowIndex - 1]?.wideCluster && { boxShadow: getRowBorder(ctx.colours.value.secondLevelRowHeaderLineColour) }),
                                    gridRow: child.rowNumber,
                                    gridColumn: columnNumber,
                                    // Pad the bottom so there's a gap below the lowest item.
                                    // The items contain their own 'above' padding.
                                    paddingBottom: "4px",
                                }}
                            >
                                {items
                                    .filter((item) => item.yGroupId === child.groupId && item.xGroupId === columnId)
                                    .map((item) => renderItem(item, /* Suppress click events, preventing accidental clicks while panning. */ panning))}
                            </div>
                        );
                        if (columnIndex === 0) {
                            rowIndex += 1;
                        }
                    });
                });
            }
        });

        return eventBlockContainers;
    }, [rows]);

    useEffect(() => {
        const { width, height } = ref.current.getBoundingClientRect();
        ctx.updateContentSize(width, height);
    }, [ref.current, items, rows]);

    useEffect(() => {
        /*
            The majority of grid rules define the grid children as having "auto" dimensions. Therefore we need to get the computed height via getBoundingClientRect
            so that we can tell the row headers how tall they need to be pixel-wise, since they're not part of the grid. In fact they're not a grid at all.
        */
        const rowHeaderLayout: RowHeaderLayout[] = [];

        singleColumnRowRefs.forEach((row) => {
            if (row.wideCluster) {
                rowHeaderLayout.push({ groupId: row.groupId, wideCluster: true, height: singleColumnElements.current[row.refIndex].getBoundingClientRect().height });
            } else {
                const children = (row as SingleColumnRowRefsNested).children.map((child) => ({
                    groupId: child.groupId,
                    height: singleColumnElements.current[child.refIndex].getBoundingClientRect().height as number,
                }));
                rowHeaderLayout.push({ groupId: row.groupId, wideCluster: false, children, height: children.reduce((sum, { height }) => sum + height, 0) });
            }
        });

        ctx.setRowHeaderLayout(rowHeaderLayout);
    }, [singleColumnRowRefs, items]);

    return (
        <div
            ref={ref}
            style={{
                position: "absolute",
                cursor: panning ? "grabbing" : "grab",
                display: "grid",
                paddingLeft: `${offsetX}px`,
                paddingTop: `${offsetY}px`,
                gridTemplateRows: gridTemplateRowsFromRowGridLayout(rows),
                gridTemplateColumns: columns.map(({ groupColumnWidth }) => groupColumnWidth).join(" "),
            }}
        >
            {columnBackgrounds}
            {eventBlockContainers}
        </div>
    );
};
